const DEFAULT_SENTRY_DSN =
  'https://273dfef5a820340f33e4831bf0834d30@o134793.ingest.us.sentry.io/4510912182484992'

export const getRuntimeEnv = (name, fallback = '') => {
  const runtimeValue = window._env_?.[name]
  if (runtimeValue && !String(runtimeValue).includes(`__${name}__`)) {
    return runtimeValue
  }

  const buildValue = process.env[name]
  if (buildValue && !String(buildValue).includes(`__${name}__`)) {
    return buildValue
  }

  return fallback
}

const parseBoolean = (value, fallback = true) => {
  if (value === undefined || value === null || value === '') return fallback
  return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase())
}

const parseNumber = (value, fallback) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const getEventText = (event, hint) => {
  const original = hint?.originalException
  const exceptionValues = event?.exception?.values || []
  const frameFiles = exceptionValues.flatMap((exception) =>
    (exception.stacktrace?.frames || []).map((frame) => frame.filename || ''),
  )

  return [
    event?.message,
    event?.logger,
    event?.transaction,
    original?.name,
    original?.message,
    ...exceptionValues.flatMap((exception) => [
      exception.type,
      exception.value,
    ]),
    ...frameFiles,
  ]
    .filter(Boolean)
    .join(' ')
}

const hasExternalFrame = (event, source) => {
  const exceptionValues = event?.exception?.values || []
  return exceptionValues.some((exception) =>
    (exception.stacktrace?.frames || []).some((frame) =>
      String(frame.filename || '').includes(source),
    ),
  )
}

const isNonActionableError = (event, hint) => {
  const error = hint?.originalException
  const message = getEventText(event, hint)
  const isApiError = event?.tags?.type === 'api_error'

  if (
    error?.name === 'RateLimitError' ||
    error?.isRateLimit ||
    message.includes('Rate limit exceeded')
  ) {
    return true
  }

  const status = error?.response?.status || error?.status
  if (!isApiError && status >= 400 && status < 500 && status !== 401) {
    return true
  }

  if (
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    message.includes('Request aborted') ||
    message.includes('canceled')
  ) {
    return true
  }

  if (
    error?.name === 'NotFoundError' ||
    (message.includes('removeChild') && message.includes('not a child'))
  ) {
    return true
  }

  if (
    error?.name === 'InvalidStateError' ||
    message.includes('Failed to start the audio device')
  ) {
    return true
  }

  if (
    message.includes('runtime.sendMessage') ||
    message.includes('SymBrowser_ModifyAnchorTagWithTarget') ||
    message.includes('SymBrowser_ModifyWindowOpenWithTarget') ||
    message.includes('ResizeObserver loop')
  ) {
    return true
  }

  if (
    hasExternalFrame(event, '/gtag/js') ||
    hasExternalFrame(event, 'googletagmanager.com/gtag/js')
  ) {
    return true
  }

  if (
    hasExternalFrame(event, 'up_loader.3.0.0.js') ||
    message.includes('ttdConversionEventsLayer')
  ) {
    return true
  }

  return false
}

export const getSentryOptions = () => {
  const environment = getRuntimeEnv('REACT_APP_ENV', 'development')
  const enabled = parseBoolean(
    getRuntimeEnv('REACT_APP_ENABLE_ERROR_TRACKING', ''),
    environment !== 'development',
  )
  const dsn = getRuntimeEnv('REACT_APP_SENTRY_DSN', DEFAULT_SENTRY_DSN)
  const release =
    getRuntimeEnv('REACT_APP_SENTRY_RELEASE') ||
    getRuntimeEnv('REACT_APP_VERSION') ||
    process.env.REACT_APP_VERSION

  return {
    enabled: enabled && Boolean(dsn),
    dsn,
    environment,
    release,
    sendDefaultPii: parseBoolean(
      getRuntimeEnv('REACT_APP_SENTRY_SEND_DEFAULT_PII', 'false'),
      false,
    ),
    tracesSampleRate: parseNumber(
      getRuntimeEnv('REACT_APP_SENTRY_TRACES_SAMPLE_RATE', ''),
      environment === 'production' ? 0.2 : 0,
    ),
    replaysSessionSampleRate: parseNumber(
      getRuntimeEnv('REACT_APP_SENTRY_REPLAY_SESSION_SAMPLE_RATE', ''),
      environment === 'production' ? 0.01 : 0,
    ),
    replaysOnErrorSampleRate: parseNumber(
      getRuntimeEnv('REACT_APP_SENTRY_REPLAY_ERROR_SAMPLE_RATE', ''),
      1.0,
    ),
  }
}

export const beforeSend = (event, hint) => {
  const pageContext = getCurrentPageContext()
  event.tags = {
    ...event.tags,
    current_path: pageContext.currentPath,
    tenant_url: event.tags?.tenant_url || pageContext.tenantUrl,
  }
  event.extra = {
    ...event.extra,
    page_url: pageContext.pageUrl,
  }

  if (isNonActionableError(event, hint)) return null
  return event
}

export const getCurrentTenantFromPath = () => {
  const [, tenantUrl] = window.location.pathname.split('/')
  return tenantUrl || 'unknown'
}

export const getCurrentRoutePath = () =>
  `${window.location.pathname}${window.location.search}`

export const getCurrentPageContext = () => ({
  tenantUrl: getCurrentTenantFromPath(),
  currentPath: getCurrentRoutePath(),
  pageUrl: window.location.href,
})
